import { GridCellParams, GridColDef } from '@mui/x-data-grid-pro';
import { useEffect, useState } from 'react';
import { Chip } from '@mui/material';
import { ComponentSpinner } from '../Loading/ComponentSpinner';
import { chipStyles, formatPrice, getFormattedDate } from '../../../utils/helpers';
import { DEFAULT_MINI_TABLE_SX_STYLES } from '../../../utils/styleHelpers';
import { BasicDataGrid } from '../Table/BasicDataGrid';
import { DetailViewCard } from '../CardWithTitle';
import { fetchSpiffItems, getSpiffSubject, getSpiffLoadingSubject, SpiffItem } from '../../../store/Spiff';
import { RecordLink } from '../../Atoms/RecordLink';

// rpm.formatted_run.run_ym is a "YYYYMM" string (e.g. "202609"), not a date -- parse it into a
// real Date so it can go through the same date formatter every other run column uses.
// Built at UTC midnight, not local midnight: getFormattedDate re-reads a date-only Date's UTC
// fields as local fields, so a locally-constructed date shifts a day (and can shift a whole
// month) in any UTC+ timezone.
const parseRunYearMonth = (runYearMonth: string): Date | null => {
  if (!runYearMonth || runYearMonth.length !== 6) return null;
  const year = Number(runYearMonth.slice(0, 4));
  const month = Number(runYearMonth.slice(4, 6));
  if (Number.isNaN(year) || Number.isNaN(month)) return null;
  return new Date(Date.UTC(year, month - 1, 1));
};

const gridCols: GridColDef[] = [
  {
    field: 'run',
    headerName: 'Run',
    flex: 0.75,
    minWidth: 75,
    sortable: false,
    filterable: false,
    renderCell: (params: GridCellParams) => {
      const date = parseRunYearMonth(params.value as string);
      if (!date) return '';
      return <span className="font-bold">{getFormattedDate(date, 'MMM YY')}</span>;
    }
  },
  {
    field: 'runStatus',
    headerName: 'Run Status',
    headerAlign: 'center',
    align: 'center',
    flex: 1,
    minWidth: 130,
    sortable: false,
    filterable: false,
    renderCell: (params: GridCellParams) => {
      const status = params.value as string;
      if (!status) return '';
      return (
        <Chip
          label={status}
          size="small"
          sx={chipStyles(status)}
          title={status}
        />
      );
    }
  },
  {
    field: 'accountNumber',
    headerName: 'Account #',
    flex: 1,
    minWidth: 120,
    sortable: false,
    filterable: false,
    renderCell: (params: GridCellParams<SpiffItem>) => {
      const accountId = params.row.accountId;
      if (!accountId) return (params.value as string) ?? '';
      return (
        <RecordLink
          title="Open RPM account"
          to={`/rpm-account/${accountId}`}>
          <div className="flex items-center text-small">{(params.value as string) ?? ''}</div>
        </RecordLink>
      );
    }
  },
  {
    field: 'supplier',
    headerName: 'Supplier',
    flex: 1,
    minWidth: 120,
    sortable: false,
    filterable: false
  },
  {
    field: 'rpmRep',
    headerName: 'RPM Rep',
    flex: 1,
    minWidth: 120,
    sortable: false,
    filterable: false,
    valueFormatter: (value: string | null) => value ?? ''
  },
  {
    field: 'product',
    headerName: 'Product',
    flex: 1,
    minWidth: 160,
    sortable: false,
    filterable: false
  },
  {
    field: 'spiff',
    headerName: 'SPIFF',
    headerAlign: 'right',
    align: 'right',
    flex: 0.75,
    minWidth: 100,
    sortable: false,
    filterable: false,
    // revenue_spiff_actual (and this line's allocationPercent) are built from gross commission,
    // not sales commission -- see upstack_commissions_api's base-run-total-query.ts.
    // allocationPercent is on a 0-100 scale, matching the BE DTO, not a 0-1 fraction.
    valueGetter: (_value, row: SpiffItem) => {
      if (row.allocationPercent === null || row.allocationPercent === undefined) return null;
      return (row.grossCommission || 0) * (row.allocationPercent / 100);
    },
    renderCell: (params: GridCellParams) => {
      if (params.value === null || params.value === undefined) return <span className="text-gray-600">—</span>;
      return <span>{formatPrice(params.value as number)}</span>;
    }
  },
  {
    field: 'noteForStaff',
    headerName: 'Note for Staff',
    flex: 1.5,
    minWidth: 180,
    sortable: false,
    filterable: false
  },
  {
    field: 'noteForAgents',
    headerName: 'Note for Agents',
    flex: 1.5,
    minWidth: 180,
    sortable: false,
    filterable: false
  },
  {
    field: 'type',
    headerName: 'Type',
    flex: 0.75,
    minWidth: 90,
    sortable: false,
    filterable: false
  }
];

interface SpiffTableProps {
  entityType: string;
  entityId: string;
}

export const SpiffTable = ({ entityType, entityId }: SpiffTableProps) => {
  const [records, setRecords] = useState<SpiffItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    // The subject is a singleton BehaviorSubject shared across every SpiffTable instance, so
    // subscribing here immediately replays whichever entity's response was last emitted (possibly
    // a different one, if the user just navigated). Clear stale rows and only accept emissions
    // that actually match this instance's current entity.
    setRecords([]);
    fetchSpiffItems(entityType, entityId);

    const spiffSub = getSpiffSubject().subscribe((data) => {
      if (data && data.entityType === entityType && data.entityId === entityId) {
        setRecords(data.spiffs);
      }
    });

    const loadingSub = getSpiffLoadingSubject().subscribe((isLoading) => {
      setLoading(isLoading);
    });

    return () => {
      if (spiffSub) spiffSub.unsubscribe();
      if (loadingSub) loadingSub.unsubscribe();
    };
  }, [entityType, entityId]);

  return (
    <DetailViewCard
      enableCollapse
      key="spiff-table-card"
      title="SPIFFs"
      dataTestId="spiff-view-card"
      className="mt-4">
      <div
        className="w-full"
        data-testid="spiff-table-container">
        {loading && <ComponentSpinner label="Loading SPIFF Data" />}
        {!loading && (
          <BasicDataGrid
            rows={records}
            columns={gridCols}
            sxStyle={DEFAULT_MINI_TABLE_SX_STYLES}
            disableColumnMenu
            showColumnVerticalBorder
            showCellVerticalBorder
            primaryKey="id"
            dataTestId="spiff-table"
          />
        )}
      </div>
    </DetailViewCard>
  );
};
